import React from 'react';
import { motion } from 'framer-motion';

const TrustedPartners = () => {
  // Using the two uploaded logos, alternating between them
  const bertelLogo = "https://customer-assets.emergentagent.com/job_534ce381-9f02-4e26-9a94-92aa4a2cb862/artifacts/fqprmz4l_Bertel_O._Steen_logo.png";
  const mollerLogo = "https://customer-assets.emergentagent.com/job_534ce381-9f02-4e26-9a94-92aa4a2cb862/artifacts/x74516yf_230924072401_112044_a53a016f59e9d7d75ce7599c82ee2c70e69377c00255e4d119a9af854bfc62d34b3bb89c4d02199e3b57a764.png";
  
  const partners = [
    { name: "Bertel O. Steen", logo: bertelLogo },
    { name: "Harald A. Møller", logo: mollerLogo },
    { name: "Møller Bil", logo: bertelLogo },
    { name: "BilXtra", logo: mollerLogo },
    { name: "Motor Gruppen", logo: bertelLogo },
    { name: "Nordvik", logo: mollerLogo },
    { name: "Pon Equipment", logo: bertelLogo },
    { name: "Viking Kontroll", logo: mollerLogo }
  ];

  return (
    <section className="bg-gradient-to-b from-white via-gray-50 to-white py-12 sm:py-16 lg:py-20 border-t border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3"
          >
            I samarbeid med
          </motion.p>
          <motion.h2 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900"
          >
            Over 2 000 forhandlere
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-2 sm:mt-3 text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Norges ledende bilforhandlere konkurrerer om å gi deg best pris
          </motion.p>
        </div>

        {/* Partner logos grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 sm:gap-6 items-center mb-10 sm:mb-14">
          {partners.map((partner, index) => (
            <motion.div
              key={partner.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.5 }}
              className="flex items-center justify-center"
            >
              <div className="group cursor-pointer transition-all duration-300 hover:scale-105 w-full">
                <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-5 shadow-sm hover:shadow-md border border-gray-100 hover:border-gray-200 transition-all">
                  <img 
                    src={partner.logo}
                    alt={partner.name}
                    className="h-10 sm:h-12 lg:h-14 w-full object-contain grayscale-[30%] group-hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                {/* Name on hover - hidden on mobile */}
                <p className="hidden sm:block text-[10px] sm:text-xs text-gray-500 text-center mt-2 opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                  {partner.name}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust indicators - Premium version */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="relative"
        >
          {/* Subtle background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-50/30 to-transparent blur-3xl"></div>
          
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 border border-gray-100 shadow-lg">
            <div className="grid grid-cols-3 gap-4 sm:gap-6 lg:gap-10">
              <div className="text-center">
                <div className="mb-2 sm:mb-3 flex justify-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl sm:rounded-2xl bg-gray-900 flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-gray-900 mb-1 sm:mb-2">2 000+</p>
                <p className="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600">Forhandlere</p>
              </div>
              
              <div className="text-center">
                <div className="mb-2 sm:mb-3 flex justify-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl sm:rounded-2xl bg-gray-900 flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-gray-900 mb-1 sm:mb-2">15 000+</p>
                <p className="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600">Biler solgt</p>
              </div>
              
              <div className="text-center">
                <div className="mb-2 sm:mb-3 flex justify-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl sm:rounded-2xl bg-gray-900 flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-gray-900 mb-1 sm:mb-2">4.8/5</p>
                <p className="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600">Vurdering</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustedPartners;
